import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import initializeMailRibbonConfiguration from '../mutators/initializeMailRibbonConfigurationMutator';
import { defaultConfig } from 'owa-mail-ribbon-store-shared-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyRibbonVersionHandler, lazyRibbonValidityCheck } from 'owa-mail-ribbon-versioning';
import { updateUserConfigurationAndService } from 'owa-session-store/lib/utils/updateUserConfigurationAndService';
import type { MailRibbonConfigStore } from 'owa-mail-ribbon-store-shared-types';
import { logUsage } from 'owa-analytics';

/**
 * Load the appropriate mail ribbon configuration for the user.
 * This function calls owa-session-store's @see {getUserConfiguration} to pull in the
 * user's persisted ribbon. If they have never persisted before, it'll be an empty string.
 */
export default async function loadMailRibbonConfiguration() {
    if (isFeatureEnabled('mon-ribbon-customization-hard-reset')) {
        hardResetRibbon();
        initializeMailRibbonConfiguration(defaultConfig);
        return;
    }

    const ribbonConfigJSON = getUserConfiguration().ViewStateConfiguration?.MailRibbonConfig;
    let ribbonConfig;

    if (!isFeatureEnabled('mon-ribbon-customization') || !ribbonConfigJSON) {
        // Default scenario of an uncustomized ribbon
        ribbonConfig = defaultConfig;
    } else {
        // User has a customized ribbon.
        ribbonConfig = JSON.parse(ribbonConfigJSON!);

        // First, check if the user's customized Ribbon config is on a higher version than the app's, which means
        // that the user is on an old version of the app.
        // This is dangerous, because this older version may not be able to parse some of the RibbonIDs in the user's config,
        // which may cause the app to crash.
        if (ribbonConfig.version > defaultConfig.version) {
            // Run the user's config through a validityChecker, and if it fails, we default to defaultConfig.
            // If it doesn't fail, then we just use the user's config as is.
            // "Failing" here means that there is some ID in the user's config that this version's defaultConfig does not use.
            // In that case, we can't be certain that the app will know how to render that RibbonID, and so we fall back to defaultConfig.
            const ribbonValidityCheck = await lazyRibbonValidityCheck.import();
            if (!ribbonValidityCheck(ribbonConfig)) {
                logUsage('RibbonVersioning_FailedValidityCheck_OlderConfigVersion', [
                    ribbonConfig.version,
                    defaultConfig.version,
                ]);
                ribbonConfig = defaultConfig;
            } else {
                logUsage('RibbonVersioning_PassedValidityCheck_OlderConfigVersion', [
                    ribbonConfig.version,
                    defaultConfig.version,
                ]);
            }
        } else {
            // The tabs below are currently uncustomizable and therefore, don't need versioning, and will always use the default version.
            // When turning on customization for these tabs, be sure to remove the tabs below so that versioning can begin.
            ribbonConfig.toolbar = defaultConfig.toolbar;
            ribbonConfig.multiline = defaultConfig.multiline;
            ribbonConfig.singleline.formatTab = defaultConfig.singleline.formatTab;
            ribbonConfig.singleline.optionsTab = defaultConfig.singleline.optionsTab;
            ribbonConfig.singleline.messageTabM3 = defaultConfig.singleline.messageTabM3;
            ribbonConfig.singleline.insertTab = defaultConfig.singleline.insertTab;
            ribbonConfig.singleline.notesTab = defaultConfig.singleline.notesTab;
            ribbonConfig.singleline.tableFormatTab = defaultConfig.singleline.tableFormatTab;
            ribbonConfig.singleline.readTab = defaultConfig.singleline.readTab;
            ribbonConfig.toolbar.readonlyTab = defaultConfig.toolbar.readonlyTab;
            ribbonConfig.contextualTabs = defaultConfig.contextualTabs;

            // Now, check if the user's customized Ribbon config is on a lower version than the app's.
            // If so, this means we want to update the user's Ribbon config via versioning.
            if (ribbonConfig.version < defaultConfig.version) {
                const ribbonVersionHandler = await lazyRibbonVersionHandler.import();
                ribbonVersionHandler(ribbonConfig);
            } else {
                // ribbonConfig.version === defaultConfig.version
                // We don't have any extra work we need to do if the ribbon versions are the same, however
                // we still want to validityCheck to safeguard engineers and users in case they end up in a bad Ribbon state.
                // If we fail this validityCheck, default back to defaultConfig.
                const ribbonValidityCheck = await lazyRibbonValidityCheck.import();
                if (!ribbonValidityCheck(ribbonConfig)) {
                    logUsage('RibbonVersioning_FailedValidityCheck_EqualConfigVersions', [
                        ribbonConfig.version,
                        defaultConfig.version,
                    ]);
                    ribbonConfig = defaultConfig;
                }
            }
        }
    }

    initializeMailRibbonConfiguration(ribbonConfig as MailRibbonConfigStore);
}

/**
 * If the flight `mon-ribbon-customization-hard-reset` is turned on, then upon load
 * the ribbon will return to the default non-customizable state.
 * This is just in case versioning causes the ribbon to become in a bad state.
 */
function hardResetRibbon() {
    const ribbonConfigString: string = JSON.stringify(defaultConfig);
    updateUserConfigurationAndService(
        config => {
            if (config.ViewStateConfiguration) {
                config.ViewStateConfiguration.MailRibbonConfig = ribbonConfigString;
            }
        },
        [{ key: 'MailRibbonConfig', valuetype: 'String', value: [ribbonConfigString] }],
        'OWA.ViewStateConfiguration'
    );
}
